/**
 * Pure route builders for the global Command Palette.
 *
 * Extracted as standalone helpers so they can be unit-tested without
 * mounting the dialog (and without a Supabase round-trip). Every search
 * result, recent group entry, and nav shortcut in CommandPalette MUST be
 * built via these helpers so the sanity tests in
 * `paletteRoutes.test.ts` can verify they always point at a real,
 * registered React Router route.
 */

import {
  Home,
  Users,
  Inbox,
  Calendar,
  Package,
  BarChart3,
  Settings,
  Search,
  CreditCard,
  FileText,
  Building2,
  PlusCircle,
} from "lucide-react";

export type HitType = "model" | "package" | "agency";

export interface PaletteNavItem {
  label: string;
  to: string;
  icon: typeof Home;
  group: string;
  keywords?: string;
}

/** Map a search hit (model / package / agency) to its public detail route. */
export function buildHitRoute(type: HitType, id: string): string {
  switch (type) {
    case "model":
      return `/profile/${id}`;
    case "package":
      return `/package/${id}`;
    case "agency":
      return `/agency/${id}`;
  }
}

/** Recent-models group always navigates to the public talent profile. */
export function buildRecentModelRoute(id: string): string {
  return `/profile/${id}`;
}

/** Nav shortcuts shown in the palette's idle state. */
export function getPaletteNavItems(isAgency: boolean): PaletteNavItem[] {
  if (isAgency) {
    return [
      { label: "Dashboard", to: "/agency/dashboard", icon: Home, group: "Navigate" },
      { label: "Models", to: "/agency/models", icon: Users, group: "Navigate" },
      { label: "Submissions", to: "/agency/submissions", icon: FileText, group: "Navigate" },
      { label: "Inbox", to: "/agency/inbox", icon: Inbox, group: "Navigate" },
      { label: "Calendar", to: "/agency/calendar", icon: Calendar, group: "Navigate" },
      { label: "Packages", to: "/agency/packages", icon: Package, group: "Navigate" },
      { label: "Clients", to: "/agency/clients", icon: Building2, group: "Navigate" },
      { label: "Analytics", to: "/agency/finance/analytics", icon: BarChart3, group: "Navigate" },
      { label: "Settings", to: "/agency/settings", icon: Settings, group: "Navigate" },
      { label: "New model", to: "/agency/models/new", icon: PlusCircle, group: "Quick actions", keywords: "create add" },
      { label: "New package", to: "/agency/packages/new", icon: PlusCircle, group: "Quick actions", keywords: "create add" },
    ];
  }
  return [
    { label: "Dashboard", to: "/dashboard", icon: Home, group: "Navigate" },
    { label: "Browse talent", to: "/browse", icon: Search, group: "Navigate", keywords: "models search" },
    { label: "Calendar", to: "/dashboard?tab=calendar", icon: Calendar, group: "Navigate" },
    { label: "Packages", to: "/dashboard?tab=packages", icon: Package, group: "Navigate" },
    { label: "Bookings", to: "/dashboard?tab=bookings", icon: CreditCard, group: "Navigate" },
    { label: "Settings", to: "/dashboard?tab=settings", icon: Settings, group: "Navigate" },
  ];
}
